import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
    Box, Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { AuthService } from '../../../services/auth.service';
import { DepartmentService } from '../../../services/department.service';
import { MaintenanceReportService, MaintenanceService } from '../../../services/maintenance.service';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import SearchableSelect from '../../common/SearchableSelect';
import {
    EQUIPMENT_OWNERSHIPS, EQUIPMENT_STATUSES, EquipmentDTO, EquipmentTypeDTO, MeterDTO,
    WORK_ORDER_STATUSES, mntLabel,
} from '../../../interfaces/maintenance';
import { Department } from '../../../interfaces/department.interface';
import { cell } from '../woCommon';
import { dhakaToday } from '../UtilityLogSheet';
import { groupLabel, useEquipmentGroups } from '../useEquipmentGroups';
import { REPORT_DEFS, ReportDef, reportKeysFor } from './reportDefs';

type Filters = Record<string, string>;

/** Pure calendar arithmetic on a yyyy-MM-dd string (no browser zone, no UTC shift of the shown day). */
export const addDays = (ymd: string, days: number): string => {
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + days));
    const p = (v: number) => String(v).padStart(2, '0');
    return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
};

const initialFilters = (def: ReportDef): Filters => {
    const today = dhakaToday();
    const f: Filters = { ...(def.defaults ?? {}) };
    // 30 days inclusive of today, the same default window the server applies
    if (def.filters.includes('from')) f.from = addDays(today, -29);
    if (def.filters.includes('to')) f.to = today;
    if (def.groupOptions) f.groupBy = def.groupOptions[0];
    if (def.bucketOptions) f.bucket = def.bucketOptions[0];
    return f;
};

/** The reports page: pick a report, set filters, Apply, then page through it or download the CSV of what is shown. */
const MaintenanceReports: React.FC = () => {
    const { report } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const institutionId = AuthService.getSelectedInstitution()!;

    const allowed = useMemo(
        () => REPORT_DEFS.filter(d => reportKeysFor(d).some(k => hasPermission(k))),
        [hasPermission],
    );
    const def = allowed.find(d => d.slug === report);

    const [draft, setDraft] = useState<Filters>({});
    const [applied, setApplied] = useState<Filters>({});
    // The report the applied filters belong to, so a switch never fires a request with the previous report's filters.
    const [appliedSlug, setAppliedSlug] = useState('');
    const [rows, setRows] = useState<unknown[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [loading, setLoading] = useState(false);
    const [equipment, setEquipment] = useState<EquipmentDTO[]>([]);
    const [types, setTypes] = useState<EquipmentTypeDTO[]>([]);
    const { groups } = useEquipmentGroups(institutionId);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [meters, setMeters] = useState<MeterDTO[]>([]);
    // The machine the user picked, kept so its label survives a new server search.
    const [pickedEquipment, setPickedEquipment] = useState<EquipmentDTO | null>(null);

    // A new report starts from its own defaults.
    useEffect(() => {
        if (!def) return;
        const f = initialFilters(def);
        setDraft(f);
        setApplied(f);
        setAppliedSlug(def.slug);
        setPage(0);
        setRows([]);
        setTotal(0);
    }, [def?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!def) return;
        if (def.filters.includes('equipmentId')) {
            MaintenanceService.listEquipment(institutionId, {}, 0, 50).then(r => setEquipment(r.content)).catch(() => setEquipment([]));
        }
        if (def.filters.includes('typeId')) {
            MaintenanceService.listTypes(institutionId).then(setTypes).catch(() => setTypes([]));
        }
        if (def.filters.includes('departmentId')) {
            DepartmentService.getLocationOptions().then(r => setDepartments(r.content ?? [])).catch(() => setDepartments([]));
        }
    }, [def?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

    // The optional meter picker lists the picked machine's meters.
    useEffect(() => {
        const eid = draft.equipmentId;
        if (!def?.filters.includes('meterId') || !eid) { setMeters([]); return; }
        MaintenanceService.getEquipment(eid).then(e => setMeters(e.meters ?? [])).catch(() => setMeters([]));
    }, [def?.slug, draft.equipmentId]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(async () => {
        if (!def) return;
        try {
            setLoading(true);
            const params: Record<string, unknown> = { ...applied };
            if (def.paged) { params.page = page; params.size = size; }
            const res = await MaintenanceReportService.get<unknown>(institutionId, def.slug, params);
            if (def.paged) {
                const p = res as { content: unknown[]; totalElements: number };
                setRows(p.content ?? []);
                setTotal(p.totalElements ?? 0);
            } else {
                const list = Array.isArray(res) ? res : [];
                setRows(list);
                setTotal(list.length);
            }
        } catch (e) {
            toast.showApiError(e, 'Failed to load the report');
        } finally {
            setLoading(false);
        }
    }, [def, applied, page, size, institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (def && appliedSlug === def.slug) load();
    }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!def) {
        const first = allowed[0];
        return first ? <Navigate to={`/maintenance/reports/${first.slug}`} replace /> : <Box sx={{ p: 3 }}>No reports available.</Box>;
    }

    const set = (k: string, v: string) => setDraft(d => ({ ...d, [k]: v }));
    const searchEquipment = (text: string) => {
        MaintenanceService.listEquipment(institutionId, { q: text }, 0, 50).then(r => setEquipment(r.content)).catch(() => undefined);
    };
    const equipmentOptions = pickedEquipment && !equipment.some(e => e.id === pickedEquipment.id)
        ? [pickedEquipment, ...equipment] : equipment;
    const apply = () => { setPage(0); setApplied({ ...draft }); };

    const download = async () => {
        try {
            // The CSV is of the APPLIED filters, not whatever is typed but not yet applied.
            const blob = await MaintenanceReportService.csv(institutionId, def.slug, applied);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${def.slug}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.showApiError(e, 'Failed to download the CSV');
        }
    };

    const has = (k: string) => (def.filters as string[]).includes(k);
    const dateField = (k: 'from' | 'to', label: string) => (
        <TextField data-help="maint.reports.date"
            key={k} size="small" type="date" label={label} value={draft[k] ?? ''} sx={cell(150)}
            InputLabelProps={{ shrink: true }} onChange={e => set(k, e.target.value)}
        />
    );
    const selectField = (k: string, label: string, options: string[], allLabel?: string, labelOf: (o: string) => string = mntLabel) => (
        <TextField data-help="maint.reports.choice" key={k} select size="small" label={label} value={draft[k] ?? ''} sx={cell(150)} onChange={e => set(k, e.target.value)}>
            {allLabel !== undefined && <MenuItem value="">{allLabel}</MenuItem>}
            {options.map(o => <MenuItem key={o} value={o}>{labelOf(o)}</MenuItem>)}
        </TextField>
    );

    return (
        <Box sx={{ p: { xs: 1, sm: 3 } }} data-help-page="maint.reports.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">Maintenance reports</Typography>
                <Button data-help="maint.reports.csv" startIcon={<DownloadIcon />} onClick={download}>Download CSV</Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }} data-help-section="maint.reports.pick">
                <TextField data-help="maint.reports.report"
                    select size="small" label="Report" value={def.slug} sx={cell(260)}
                    onChange={e => navigate(`/maintenance/reports/${e.target.value}`)}
                >
                    {allowed.map(d => <MenuItem key={d.slug} value={d.slug}>{d.label}</MenuItem>)}
                </TextField>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{def.blurb}</Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }} data-help-section="maint.reports.filter">
                {has('from') && dateField('from', 'From')}
                {has('to') && dateField('to', 'To')}
                {def.bucketOptions && has('bucket') && selectField('bucket', 'Bucket', def.bucketOptions)}
                {def.groupOptions && has('groupBy') && selectField('groupBy', 'Group by', def.groupOptions)}
                {has('days') && (
                    <TextField data-help="maint.reports.lookback"
                        size="small" type="number" label="Look back (days)" value={draft.days ?? ''} sx={cell(150)}
                        inputProps={{ min: 1, max: 366 }} onChange={e => set('days', e.target.value)}
                    />
                )}
                {has('limit') && (
                    <TextField data-help="maint.reports.machinesShown"
                        size="small" type="number" label="Machines to show" value={draft.limit ?? ''} sx={cell(150)}
                        inputProps={{ min: 1, max: 100 }} onChange={e => set('limit', e.target.value)}
                    />
                )}
                {has('equipmentId') && (
                    <Box data-help="maint.reports.machine" sx={cell(220)}>
                        <SearchableSelect
                            options={equipmentOptions} value={draft.equipmentId ?? ''} size="small" label="Machine"
                            getOptionId={e => e.id} getOptionLabel={e => `${e.code} ${e.name}`}
                            disableClientFilter onInputChange={searchEquipment}
                            onChange={id => {
                                setPickedEquipment(equipment.find(e => e.id === id) ?? null);
                                setDraft(d => ({ ...d, equipmentId: id, meterId: '' }));
                            }}
                        />
                    </Box>
                )}
                {has('meterId') && (
                    <Box data-help="maint.reports.meter" sx={cell(200)}>
                        <SearchableSelect
                            options={meters} value={draft.meterId ?? ''} size="small" label="Meter (optional)"
                            getOptionId={m => m.id} getOptionLabel={m => `${m.code} ${m.name}`}
                            onChange={id => set('meterId', id)}
                        />
                    </Box>
                )}
                {has('departmentId') && (
                    <Box data-help="maint.reports.line" sx={cell(200)}>
                        <SearchableSelect
                            options={departments} value={draft.departmentId ?? ''} size="small" label="Line / department"
                            getOptionId={d => String(d.id)} getOptionLabel={d => d.name}
                            onChange={id => set('departmentId', id)}
                        />
                    </Box>
                )}
                {has('typeId') && (
                    <Box data-help="maint.reports.type" sx={cell(200)}>
                        <SearchableSelect
                            options={types} value={draft.typeId ?? ''} size="small" label="Type"
                            getOptionId={x => x.id} getOptionLabel={x => `${x.code} ${x.name}`}
                            onChange={id => set('typeId', id)}
                        />
                    </Box>
                )}
                {has('status') && def.slug === 'breakdown-log' && selectField('status', 'Status', [...WORK_ORDER_STATUSES], 'All')}
                {has('status') && def.slug === 'equipment-register' && selectField('status', 'Status', EQUIPMENT_STATUSES, 'All')}
                {has('category') && selectField('category', 'Group', groups.map(g => g.code), 'All', groupLabel)}
                {has('ownership') && selectField('ownership', 'Ownership', EQUIPMENT_OWNERSHIPS, 'All')}
                {has('idleOnly') && (
                    <FormControlLabel data-help="maint.reports.idle"
                        sx={{ flex: '1 1 150px', maxWidth: 290, m: 0 }}
                        control={<Checkbox checked={draft.idleOnly === 'true'} onChange={e => set('idleOnly', e.target.checked ? 'true' : '')} />}
                        label="Idle machines only"
                    />
                )}
                <Button data-help="maint.reports.apply" variant="contained" onClick={apply}>Apply</Button>
            </Box>

            <Paper data-help-section="maint.reports.table">
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {def.columns.map(c => <TableCell key={c.header} align={c.numeric ? 'right' : 'left'}>{c.header}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 && (
                                    <TableRow><TableCell colSpan={def.columns.length} align="center">No data for these filters</TableCell></TableRow>
                                )}
                                {rows.map((r, i) => (
                                    <TableRow key={i} hover>
                                        {def.columns.map(c => (
                                            <TableCell key={c.header} align={c.numeric ? 'right' : 'left'}>{c.value(r)}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {def.paged && (
                    <TablePagination
                        component="div" count={total} page={page} rowsPerPage={size} rowsPerPageOptions={[25, 50, 100]}
                        onPageChange={(_e, p) => setPage(p)}
                        onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                )}
            </Paper>
        </Box>
    );
};

export default MaintenanceReports;
